import { TimeUnit } from './apollo/graphqlSchema';
import { getCapitalizedTimeUnitText } from './timeUnits';
import { MaybeNil } from './interfaces';
import {
  defaultNumberFormatOptions,
  globalFormat,
  maximumFractionDigits,
} from './formatting';
import { countDigitsAfterDecimal } from './numbers';

export const formatTimeUnit = (unit?: TimeUnit): string =>
  getCapitalizedTimeUnitText(unit ?? TimeUnit.Day).singular;

export const formatWordsFromUpperCase = (words: string) => {
  if (!words || words.length === 0) {
    return 'Day';
  }
  return words.charAt(0) + words.substring(1).toLowerCase();
};

export const truncateStringWithLength = (
  originString: string,
  truncateLength: number,
) => {
  if (originString.length <= truncateLength) {
    return originString;
  }
  return originString.substring(0, truncateLength - 3) + '...';
};

export const formatNumberWithComma = (
  rawNumber: string | number,
): string | number => {
  if (typeof rawNumber === 'number') {
    if (rawNumber % 1 === 0) {
      return globalFormat.formatNumber(
        rawNumber,
        defaultNumberFormatOptions.integer,
      );
    } else {
      const digitsAfterDecimal = countDigitsAfterDecimal(rawNumber.toString());
      return globalFormat.formatNumber(rawNumber, {
        style: 'decimal',
        decimalPlace: Math.min(digitsAfterDecimal, maximumFractionDigits),
      });
    }
  }

  let res: string = rawNumber;
  let isPercentageNumber = false;
  if (res.includes('%')) {
    isPercentageNumber = true;
    res = res.replace('%', '');
  }

  if (isNaN(res as any)) {
    return rawNumber;
  }
  if (res.includes('.')) {
    const digitsAfterDecimal = countDigitsAfterDecimal(res.toString());
    res = globalFormat.formatNumber(res, {
      style: 'decimal',
      decimalPlace: Math.min(digitsAfterDecimal, maximumFractionDigits),
    });
  } else {
    res = globalFormat.formatNumber(res, defaultNumberFormatOptions.integer);
  }
  if (isPercentageNumber) {
    res += '%';
  }
  return res;
};

/**
 * Formats a number as tiny as possible
 *
 * @param n - the number to format
 * @param decimalPlace - optional number of decimal places if number is between 0 and 100
 */
export const formatValueTiny = (n: number, decimalPlace?: MaybeNil<number>) => {
  if (n < 0) {
    return '<0';
  } else if (n === 0) {
    return '0';
  } else if (n < 0.01) {
    return (
      '<' +
      globalFormat.formatNumber(0.01, {
        style: 'decimal',
        decimalPlace: 2,
      })
    );
  } else if (n < 1e1) {
    return globalFormat.formatNumber(n, {
      style: 'decimal',
      notation: 'compact',
      minimumFractionDigits: decimalPlace ?? 0,
      maximumFractionDigits: decimalPlace ?? 2,
    });
  } else if (n < 1e2) {
    return globalFormat.formatNumber(n, {
      style: 'decimal',
      notation: 'compact',
      minimumFractionDigits: decimalPlace ?? 0,
      maximumFractionDigits: decimalPlace ?? 1,
    });
  } else if (n < 1e3) {
    return globalFormat.formatNumber(n, {
      style: 'decimal',
      notation: 'compact',
      decimalPlace: 0,
    });
  } else if (n >= 1e3 && n < 1e12) {
    return globalFormat.formatNumber(n, {
      style: 'decimal',
      notation: 'compact',
      decimalPlace: 1,
    });
  } else {
    return '>1T';
  }
};

/**
 * Formats a fraction value as percent tiny as possible. Rounds to the nearest whole percent
 *
 * @param n - the number to format
 * @param decimalPlace - optional number of decimal places if number is between 0 and 100
 */
export const formatPercentTiny = (n: number, decimalPlace?: MaybeNil<number>) =>
  `${formatValueTiny(n * 100, decimalPlace)}%`;

// returns a string in singular/plural form depending on the number of items
export function pluralize(singular: string, itemsCount: number): string {
  return `${singular}${itemsCount !== 1 ? 's' : ''}`;
}
